import type { TFunction } from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBoxBase from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputCalendarCustom from "../../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000Label from "../../../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { useU3121FindEmployeeHandlers } from "../../../../U3121UseFindHandlers";
import type { U3121Task } from "../../../../U3121Service";
import type { U3121SubProjectEmployeeAssignmentHandlerReturn } from "./U3121SubProjectEmployeeAssignmentHandlers";

interface U3121SubProjectEmployeeAssignmentEditDlgProps {
  handler: U3121SubProjectEmployeeAssignmentHandlerReturn;
  t: TFunction;
  NS: string;
  taskOptions?: U3121Task[];
}

type TaskOption = U3121Task & { label: string } & Record<string, unknown>;

function getTaskLabel(task: U3121Task, language: string): string {
  if (language === "fr" && task.taskDescriptionFr?.trim()) return task.taskDescriptionFr.trim();
  if (language === "en" && task.taskDescriptionEn?.trim()) return task.taskDescriptionEn.trim();
  return task.taskDescription?.trim() || task.taskCode?.trim() || task.id;
}

function U3121SubProjectEmployeeAssignmentEditDlg({ handler, t, NS, taskOptions = [] }: U3121SubProjectEmployeeAssignmentEditDlgProps) {
  const { formStateHandlers } = handler;
  const { i18n } = useTranslation();
  const findEmployeeHandlers = useU3121FindEmployeeHandlers();
  const language = i18n.language;

  const onSearchEmployee = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findEmployeeHandlers.search(criterias),
    [findEmployeeHandlers],
  );

  const displayEmployee = useCallback((item: Record<string, unknown>) => {
    const firstName = item.firstName as string;
    const lastName = item.lastName as string;
    const codeEmployee = item.codeEmployee as string;
    return `${firstName} ${lastName} (${codeEmployee})`;
  }, []);

  const extractDataForEmployee = useCallback((result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "employee.id", value: result.id },
        { fieldName: "employee.firstName", value: result.firstName },
        { fieldName: "employee.lastName", value: result.lastName },
        { fieldName: "employee.codeEmployee", value: result.codeEmployee },
      ];
    }
    return [
      { fieldName: "employee.id", value: null },
      { fieldName: "employee.firstName", value: null },
      { fieldName: "employee.lastName", value: null },
      { fieldName: "employee.codeEmployee", value: null },
    ];
  }, []);

  const displayTaskLabel = useCallback(
    (task: U3121Task) => {
      const linguisticLabel = getTaskLabel(task, language);
      const code = task.taskCode?.trim();
      return code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
    },
    [language],
  );

  const taskData = useMemo<TaskOption[]>(() => taskOptions.map((task) => ({ ...task, label: displayTaskLabel(task) })), [taskOptions, displayTaskLabel]);

  const formData = formStateHandlers.getData();
  const isDisabled = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasEmployeeOrTask = Boolean(
    String(formData?.employee?.id ?? "").trim() || String(formData?.task?.id ?? "").trim(),
  );
  const hasTaskError = Boolean(formStateHandlers.getErrors()["task.id"] && (formStateHandlers.getTouched()["task"] || formStateHandlers.isSubmitted()));

  const handleTaskChange = (item: TaskOption | null) => {
    formStateHandlers.setData({
      ...formData,
      task: item ?? undefined,
    });
  };

  return (
    <U2000SaveCancelDialog isOpen={handler.isDialogOpen} height="h-[875px]" onClose={handler.onDlgClose} title={t("lblEmployeeBillingAssignment", { ns: NS })}>
      <div className="grid grid-cols-1 gap-4">
        <U2000AutoCompleteInput
          formStateHandlers={formStateHandlers}
          fieldName="employee"
          mainFieldName="codeEmployee"
          entityLink="U3101"
          label={t("lblEmployee", { ns: NS })}
          onSearch={onSearchEmployee}
          displayFn={displayEmployee}
          overrideExtractDataFn={extractDataForEmployee}
          minCarLaunchSearch={1}
          resultLabels={[]}
          resultFields={["firstName", "lastName", "codeEmployee"]}
        />

        <div className="space-y-1.5">
          <U2000Label label={t("lblTask", { ns: NS })} htmlFor="task" />
          <div className={hasTaskError ? "[&>div>button]:border-red-300 [&>div>button:focus]:border-red-500 [&>div>button:focus]:ring-red-500/20" : ""}>
            <U2000ComboBoxBase<TaskOption>
              data={taskData}
              keyField="id"
              descField="label"
              value={formData?.task ? { ...formData.task, label: displayTaskLabel(formData.task) } : null}
              onChangeObject={handleTaskChange as (item: TaskOption | null) => void}
              disabled={isDisabled}
              maxHeight={250}
              noneItemLabel={t("Aucune sélection", { ns: "common" })}
            />
          </div>
          <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="task.id" />
        </div>

        <U2000InputCurrency
          formStateHandlers={formStateHandlers}
          fieldName="hourRate"
          label={t("lblHourlyRate", { ns: NS })}
          decimalDigits={2}
          readonly={isDisabled || !hasEmployeeOrTask}
          required={hasEmployeeOrTask && !isDisabled}
        />

        <U2000InputCalendarCustom popupPosition="auto" formStateHandlers={formStateHandlers} fieldName="validFrom" label={t("lblValidFrom", { ns: NS })} required />
        <U2000InputCalendarCustom popupPosition="top" formStateHandlers={formStateHandlers} fieldName="validTo" label={t("lblValidTo", { ns: NS })} />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3121SubProjectEmployeeAssignmentEditDlg;
